// Content-Aware Move Tool — move or extend a selection with content-aware fill behind.
// Photoshop equivalent: Content-Aware Move Tool (J, under Spot Healing group).

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { mergeBBox, stampLine, type BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type ContentAwareMoveMode = "move" | "extend";

export class ContentAwareMoveTool implements Tool {
  readonly id = "content-aware-move";
  readonly shortcut = "J";

  mode: ContentAwareMoveMode = "move";
  adaptation: number = 5; // 1-7, how well the fill blends
  sampleAllLayers = false;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    selMask: Uint8Array;
    selWidth: number;
    selHeight: number;
    offsetX: number;
    offsetY: number;
    bbox: BBox | null;
    lastDx: number;
    lastDy: number;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array; width?: number; height?: number } | null;
    if (!sel?.mask || !sel.width || !sel.height) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      selMask: new Uint8Array(sel.mask),
      selWidth: sel.width,
      selHeight: sel.height,
      offsetX: 0,
      offsetY: 0,
      bbox: null,
      lastDx: Math.round(docX),
      lastDy: Math.round(docY),
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = Math.round(p.x - this.active.layer.x);
    const docY = Math.round(p.y - this.active.layer.y);

    const dx = docX - this.active.lastDx;
    const dy = docY - this.active.lastDy;

    if (dx === 0 && dy === 0) return;

    this.active.offsetX += dx;
    this.active.offsetY += dy;
    this.active.lastDx = docX;
    this.active.lastDy = docY;

    // Restore original, then move selection content
    this.applyMove(ctx);
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;

    const { layer, layerSnapshot, bbox } = this.active;
    if (bbox) {
      const after = snapshotRegion(layer, bbox);
      const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
      ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
    }

    this.active = null;
  }

  private applyMove(ctx: ToolContext): void {
    if (!this.active) return;
    const { layer, layerSnapshot, selMask, selWidth, selHeight, offsetX, offsetY } = this.active;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;

    // Restore original pixels
    data.set(layerSnapshot);

    // Compute selection bounding box
    let minX = selWidth, minY = selHeight, maxX = -1, maxY = -1;
    for (let y = 0; y < selHeight; y++) {
      for (let x = 0; x < selWidth; x++) {
        if (selMask[y * selWidth + x]! > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0) return;

    // Move selected pixels
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (selMask[y * selWidth + x] === 0) continue;

        const srcIdx = (y * selWidth + x) * 4;
        const dstX = x + offsetX;
        const dstY = y + offsetY;

        if (dstX < 0 || dstX >= w || dstY < 0 || dstY >= h) continue;

        const dstIdx = (dstY * w + dstX) * 4;
        data[dstIdx] = layerSnapshot[srcIdx]!;
        data[dstIdx + 1] = layerSnapshot[srcIdx + 1]!;
        data[dstIdx + 2] = layerSnapshot[srcIdx + 2]!;
        data[dstIdx + 3] = layerSnapshot[srcIdx + 3]!;

        // Clear source (for move mode)
        if (this.mode === "move") {
          data[srcIdx + 3] = 0;
        }
      }
    }

    // Content-aware fill for the vacated area (simplified: blend edges)
    if (this.mode === "move") {
      this.contentAwareFill(data, w, h, selMask, selWidth, selHeight, offsetX, offsetY);
    }

    layer.pixels.markDirty();

    // Update bbox for undo
    const touchBBox = this.computeTouchBBox(w, h, selMask, selWidth, selHeight, offsetX, offsetY);
    if (touchBBox) {
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, touchBBox) : touchBBox;
    }
  }

  private contentAwareFill(
    data: Uint8ClampedArray, w: number, h: number,
    selMask: Uint8Array, sw: number, sh: number,
    offsetX: number, offsetY: number,
  ): void {
    // Simplified content-aware fill: for cleared pixels, sample nearby non-cleared pixels
    const fillRadius = this.adaptation * 3 + 5;

    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        if (selMask[y * sw + x] === 0) continue;

        const dstX = x + offsetX;
        const dstY = y + offsetY;
        if (dstX < 0 || dstX >= w || dstY < 0 || dstY >= h) continue;

        // Check if source position is now empty (was moved)
        const srcIdx = (y * sw + x) * 4;
        if (data[srcIdx + 3] === 0) {
          // Find nearest non-empty pixel
          let r = 0, g = 0, b = 0, a = 0, count = 0;
          for (let dy = -fillRadius; dy <= fillRadius && count < 8; dy++) {
            for (let dx = -fillRadius; dx <= fillRadius && count < 8; dx++) {
              const nx = x + dx;
              const ny = y + dy;
              if (nx < 0 || nx >= sw || ny < 0 || ny >= sh) continue;
              if (selMask[ny * sw + nx] === 0) continue; // skip other moved pixels

              const nIdx = (ny * sw + nx) * 4;
              if (data[nIdx + 3]! > 0) {
                const dist = Math.sqrt(dx * dx + dy * dy);
                const weight = 1 / (1 + dist);
                r += data[nIdx]! * weight;
                g += data[nIdx + 1]! * weight;
                b += data[nIdx + 2]! * weight;
                a += data[nIdx + 3]! * weight;
                count++;
              }
            }
          }

          if (count > 0) {
            data[srcIdx] = Math.round(r / count);
            data[srcIdx + 1] = Math.round(g / count);
            data[srcIdx + 2] = Math.round(b / count);
            data[srcIdx + 3] = Math.round(a / count);
          }
        }
      }
    }
  }

  private computeTouchBBox(
    w: number, h: number,
    selMask: Uint8Array, sw: number, sh: number,
    offsetX: number, offsetY: number,
  ): BBox | null {
    let minX = w, minY = h, maxX = -1, maxY = -1;
    let found = false;

    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        if (selMask[y * sw + x] === 0) continue;
        found = true;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x + offsetX > maxX) maxX = x + offsetX;
        if (y + offsetY > maxY) maxY = y + offsetY;
      }
    }

    if (!found) return null;
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
